import React from 'react';
import Icon from '../graphics/Icon';
import Grade from '../graphics/Grade';
import List, { ListItem, ListItemText } from 'material-ui/List';
import 'simplebar';
import 'simplebar/dist/simplebar.css';
import styled from 'styled-components';

const Help = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 370;
  background: #FEEDCB;
  color: #474747;
  font-weight: 100;
  top: 0;
  opacity: ${props => props.opacity };
  transition: all 0.5s linear;

  @media (orientation: landscape) {
    margin-left: 50%;
    width: 50%;
  }
  @media (min-width: 700px) and (orientation: landscape) {
    margin-left: 58.33333%;
    width: 41.66667%;
  }
  @media (min-width: 960px) and (orientation: landscape) {
    margin-left: 58.33333%;
    width: 41.66667%;
    height: calc(100% - 62px);
  }
  @media (min-width: 1920px) and (orientation: landscape) {
    margin-left: 66.66667%;
    width: 33.33333%;
  }

  h1 {
    color: #0066FF;
    margin-top: 0;
  }
  p {
    line-height: 1.4em;
  }
  li {
    padding-left: 0;
    padding-right: 0;
    margin-bottom: 10px;
  }
`;
const CloseBtn = styled.div`
  position: absolute;
  right: 20px;
  top: 20px;
  cursor: pointer;
  z-index: 220;
`;
const Inner = styled.div`
  margin: 0 0 0 25px;
  height: 100%;

  @media (min-width: 600px) {
    margin: 0 0 0 25px;
  }
  @media (min-width: 960px) {
    margin: 0 0 0 55px;
  }
  @media (min-width: 600px) and (orientation: portrait) {
    margin: 0 0 0 55px;
  }
  @media (min-width: 960px) and (orientation: portrait) {
    margin: 0 0 0 85px;
  }
`;
const Scroll = styled.div`
  height: 100%;
`;
const Content = styled.div`
  padding: 40px 40px 30px 20px;
  @media (min-width: 600px) {
    padding: 60px 60px 50px 0;
  }
  @media (min-width: 960px) {
    padding: 80px 60px 65px 0;
  }
  @media (min-width: 1280px) {
    padding: 100px 60px 80px 0;
  }
`;
const ListText = styled.div`
  margin-left: 16px;
  font-weight: 100;
`;

const help = (props) => {
  return (
      <Help opacity={props.opacity}>
        <CloseBtn onClick={props.closeHelp}><Icon name="closeOrange" /></CloseBtn>
        <Inner>
          <Scroll data-simplebar>
            <Content>
              <h1>Help</h1>
              <p>To get started, enter the location of your favorite beach in the search bar or play around with the map to find water quality information
              for beaches near you. Below is a guide to help you figure out what is going on at the beach. </p>
              <h2>NowCast Predictions</h2>
              <p>Predictive models are being used to forecast daily water quality at select beaches around California. NowCast predictions are made every morning, and are the most up-to-date water quality information available for the beach. Use the ‘NowCast’ filter at the top to display only NowCast predictions.</p>
              <List>
                <ListItem>
                  <Grade grade="good" />
                  <ListText>
                    <b>NowCast Prediction – Good</b><br/>
                    Good water quality is predicted today based on the most recent environmental conditions observed by a variety of trusted scientific sources. 
                    This means that bacteria levels are not likely to exceed health standards for ocean water contact.
                  </ListText>
                </ListItem>
                <ListItem>
                  <Grade grade="poor" />
                  <ListText>
                    <b>NowCast Prediction – Poor</b><br/>
                    Poor water quality is predicted today based on the most recent environmental conditions observed by a variety of trusted scientific sources. 
                    This means that bacteria levels are likely to exceed health standards for ocean water contact.
                  </ListText>
                </ListItem>
              </List>
              <h2>Beach Report Card</h2>
              <p>Beach Report Card grades follow the familiar A-F school grading system and are calculated for each location in California based on exceedances of state water quality standards. Grades are an overall indicator of water quality at the beach during the associated <a href="#gradeperiod" onClick={props.openFAQ}>grade period</a>. 
                Simply put, the higher the grade a beach receives, the better the water quality at that beach, and the lower the risk of getting sick from water contact. Use the ‘Grades’ filter at the top to display only Beach Report Card grades.</p>
              <br />
              <h3>Beach Grade Key:</h3>
              <List>
                <ListItem>
                  <Grade grade="A" />
                  <ListText>
                    <b>A+ Grade</b> (100 points)<br/>
                    Excellent, with no exceedance of thresholds over the grading time period.<br/><br/>
                    <b>A Grade</b> (90-99 points): Excellent
                  </ListText>
                </ListItem>
                <ListItem>
                  <Grade grade="B" />
                  <ListText>
                    <b>B Grade</b> (80-89 points): <br/>Very good
                  </ListText>
                </ListItem>
                <ListItem>
                  <Grade grade="C" />
                  <ListText>
                    <b>C Grade</b> (70-79 points): <br/>Average
                  </ListText>
                </ListItem>
                <ListItem>
                  <Grade grade="D" />
                  <ListText>
                    <b>D Grade</b> (60-69 points) <br/>Poor
                  </ListText>
                </ListItem>
                <ListItem>
                  <Grade grade="F" />
                  <ListText>
                    <b>F Grade</b> ({"<=59"} points): <br/>Very Poor
                  </ListText>
                </ListItem>
              </List>
              <ListItem>
                <Grade grade="ns" />
                <ListText><b>ns - no sample:</b> <br/>No samples for the grading period are available</ListText>
              </ListItem>
            The Beach Report Card relies on our beach agency partners across the West Coast of the United States for water quality sampling data. Current grades can only be calculated if the most recent sampling data is available.<br/><br/>
              Reasons why you may see ‘ns’ at the beach include:
              <ol>
              <li> The beach is not typically sampled outside of the summer swim season.</li>
              <li> Samples were not taken because the sampling station was not accessible, possibly due to unsafe conditions or temporary habitat protection.</li>
              <li> There was an error at the lab processing the sample, voiding the results.</li>
              <li> The sampling agency has yet to send the latest data to the Beach Report Card team.</li>
              <li> The beach is outside our current sampling zone of California, Oregon and Washington.</li>
              <li> The conditions for a particular grade type did not exist. For example, the wet weather grade will be unavailable if there were no rain events during the grading period.</li>
              </ol>
            <p>If you'd like to see water quality grades for your favorite beach, please <a href="https://healthebay.org/contact/" target="_blank">contact us</a>.</p>

              <h3>Alerts</h3>
            <p>Certain beaches will be under alert due to acute pollution events like significant rainfall and sewage spills. Heal the Bay tracks these events based on information provided by local health agencies.</p>

              <b className="with-icon"><Grade grade="Rain Advisory" /> Rain Advisory:</b>
            <p>The beach is under advisory due to significant rainfall in the past 72 hours. Water quality is typically poor at beaches impacted by runoff from rainfall, and it is advised to take caution or avoid swimming at this beach for 3 days after a rain event. Use the ‘Alerts’ filter at the top of the page to display beaches under alert.</p>
              
              <ListItem>
                <Grade grade="Closure" />
                <ListText><b>Closure:</b> <br/>The beach has been closed by the local health agency</ListText>
              </ListItem>
              <p>When a beach is closed, the local health agency will:</p>
              <ol>
                <li>Notify the media.</li>
                <li>Establish a telephone hotline number to inform the public.</li>
                <li>Close the beach to all forms of water contact.</li>
              </ol>
              <p>In addition, the beach status is affected as follows:</p>
              <ol>
                <li>The beach is immediately closed.</li>
              <li>The beach remains closed until the source has been identified by the local health agency, the spill ceases, and sampling results indicate compliance with state water quality standards.</li>
              </ol>

              <h2>FAQ</h2>
              <a href="javascript:void(0)" onClick={props.openFAQ}>View FAQ</a>
              <h2><a href="https://healthebay.org/contact/" target="_blank">Contact Us</a></h2>
              
            </Content>
          </Scroll>
        </Inner>
      </Help>
  );
}

export default help;


// WEBPACK FOOTER //
// ./src/components/static/Help.js